import React from 'react';
import { Link } from 'react-router-dom';
import { api } from '../services/api';
import { useApiData } from '../hooks/useApiData';
import { SeriesBars, MetricBar, ScoreCell, LoadingState, ErrorState } from './primitives';
import { rangeLabel } from './RangeControl';
import { ClientUser, RangeRep, SeriesField, SeriesPoint, TimeRange } from '../types/superadmin';
import { RUBRIC_DIMENSIONS } from '../config/rubricDimensions';
import { repSessionsPath } from '../lib/rangeParams';
import { timeAgo } from '../lib/format';

// Summary stats in the expanded panel: overall Call quality, then each Lushin
// rubric dimension. (Live-intelligence dims + Training are off for Lushin.)
const DIMENSIONS: Array<{ key: keyof RangeRep; label: string; bar?: boolean }> = [
  { key: 'callQualityScore', label: 'Call quality', bar: true },
  ...RUBRIC_DIMENSIONS.map((d) => ({ key: d.key as keyof RangeRep, label: d.short }))
];

// All by-day charts shown in the accordion. Count charts auto-scale; score
// charts are fixed to 0-100 and render '%'. Same date format as each other.
const CHARTS: Array<{ field: SeriesField; title: string; unit?: string; score?: boolean }> = [
  { field: 'minutes', title: 'Minutes by day', unit: ' min' },
  { field: 'sessions', title: 'Sessions by day' },
  { field: 'callQualityScore', title: 'Call quality by day', unit: '%', score: true },
  ...RUBRIC_DIMENSIONS.map((d) => ({
    field: d.key as SeriesField,
    title: `${d.short} by day`,
    unit: '%',
    score: true
  }))
];

function ChartBlock({
  data,
  chart,
  range,
  repName
}: {
  data: SeriesPoint[];
  chart: { field: SeriesField; title: string; unit?: string; score?: boolean };
  range: TimeRange;
  repName: string;
}) {
  return (
    <div className="chart-block">
      <div className="section-head">
        <div>
          <h3>{chart.title}</h3>
          <p className="desc">
            {repName} · {rangeLabel(range)}.
          </p>
        </div>
      </div>
      <SeriesBars data={data} field={chart.field} unit={chart.unit} maxValue={chart.score ? 100 : undefined} />
    </div>
  );
}

/** Account facts from the client's live database — absent for a rollup-only rep. */
function AccountFacts({ user }: { user: ClientUser }) {
  return (
    <div className="rep-panel-facts">
      <div className="rep-panel-fact">
        <div className="lbl">Role</div>
        <span className={'chip ' + (user.role === 'admin' ? 'info' : '')}>{user.role}</span>
      </div>
      <div className="rep-panel-fact">
        <div className="lbl">Status</div>
        <span className={'chip ' + (user.isActive ? 'pos' : 'neg')}>
          {user.isActive ? 'active' : 'deactivated'}
        </span>
      </div>
      <div className="rep-panel-fact">
        <div className="lbl">Last login</div>
        <span>{timeAgo(user.lastLogin)}</span>
      </div>
      <div className="rep-panel-fact">
        <div className="lbl">Added</div>
        <span>{timeAgo(user.createdAt)}</span>
      </div>
      <div className="rep-panel-fact">
        <div className="lbl">Email</div>
        <span className="mono" style={{ color: 'var(--ink-2)' }}>
          {user.email}
        </span>
      </div>
    </div>
  );
}

/**
 * Expanded per-user panel: their account details, their dimension scores for
 * the range, a link into their individual calls, then one by-day chart per
 * metric (Minutes, Sessions, and each score), all in the same date format and
 * gap-filled across the range. Series is lazy-loaded when the row is expanded.
 *
 * `rep` is always present — the users table synthesises a zeroed row for
 * someone with no activity in the range, so this panel never has to branch on
 * "has metrics".
 *
 * `user` is three-valued on purpose. `undefined` means the caller never looked
 * the account up (the platform-wide Reps page, which spans clients); `null`
 * means it looked and there is none. Only the second is worth telling the
 * reader about — collapsing them would put "this account was deleted" under
 * every row on a page that simply never fetched rosters.
 */
export default function RepSeriesPanel({
  rep,
  user,
  range
}: {
  rep: RangeRep;
  user?: ClientUser | null;
  range: TimeRange;
}) {
  const { data, loading, error } = useApiData(
    () => api.getRepSeries(rep.tenantId, rep.userId, range),
    [rep.tenantId, rep.userId, JSON.stringify(range)]
  );

  const series = data?.data.series ?? [];

  return (
    <div className="rep-panel">
      {/* Always two children, so the link stays right-aligned even with no
          account facts to sit beside it. */}
      <div className="rep-panel-head">
        <div>
          {user ? <AccountFacts user={user} /> : null}
          {user === null && (
            <p className="desc" style={{ margin: 0 }}>
              This user has activity in the rollup but no account in the client&rsquo;s database any
              more, so there are no account details to show.
            </p>
          )}
        </div>
        {/* A rep with no userId cannot address a session list — the route
            would not match — so the way in is simply not offered. */}
        {rep.userId && (
          <Link className="btn btn-primary" to={repSessionsPath(rep.tenantId, rep.userId, range)}>
            Review sessions →
          </Link>
        )}
      </div>

      <div className="rep-panel-scores">
        {DIMENSIONS.map(({ key, label, bar }) => {
          const value = rep[key] as number | null;
          return (
            <div className="rep-panel-score" key={String(key)}>
              <div className="lbl">{label}</div>
              {bar ? <MetricBar value={value} /> : <ScoreCell value={value} />}
            </div>
          );
        })}
        {/* Training is off for Lushin — re-enable when training turns on:
        <div className="rep-panel-score">
          <div className="lbl">Training sessions</div>
          <span className="metric-num">{fmtInt(rep.trainingSessions)}</span>
        </div> */}
      </div>

      {loading && <LoadingState label="Loading rep activity…" />}
      {error && <ErrorState message={error} />}
      {!loading && !error && (
        <div className="chart-grid">
          {CHARTS.map((chart) => (
            <ChartBlock key={chart.field} data={series} chart={chart} range={range} repName={rep.name} />
          ))}
        </div>
      )}
    </div>
  );
}
